import { useCallback } from 'react';
import api from '../services/api';
import { useApi } from './useApi';

interface Domain {
  _id: string;
  user: string;
  domainName: string;
  appPort: number;
  description?: string;
  nginxConfigured: boolean;
  sslStatus: 'none' | 'pending' | 'active' | 'failed' | 'expired';
  dnsVerified: boolean;
  status: 'pending' | 'provisioning' | 'active' | 'failed' | 'suspended';
  provisioningLog: Array<{
    step: string;
    status: string;
    message: string;
    timestamp: string;
  }>;
  createdAt: string;
}

interface DomainListResult {
  domains: Domain[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface DnsVerifyResult {
  verified: boolean;
  serverIp: string;
  resolvedIps: string[];
  domain: Domain;
}

interface SslStatusResult {
  domainName: string;
  sslStatus: string;
  certExists: boolean;
  sslIssuedAt: string | null;
  sslExpiresAt: string | null;
  daysUntilExpiry: number | null;
}

export function useListDomains() {
  const fetchFn = useCallback(
    async (page = 1, limit = 10, status?: string): Promise<DomainListResult> => {
      const params: Record<string, any> = { page, limit };
      if (status) params.status = status;
      const { data } = await api.get('/domains', { params });
      return data.data;
    },
    [],
  );

  return useApi<DomainListResult>(fetchFn);
}

export function useGetDomain() {
  const fetchFn = useCallback(async (id: string): Promise<Domain> => {
    const { data } = await api.get(`/domains/${id}`);
    return data.data.domain;
  }, []);

  return useApi<Domain>(fetchFn);
}

export function useRegisterDomain() {
  const registerFn = useCallback(
    async (domainName: string, appPort: number, description?: string): Promise<Domain> => {
      const { data } = await api.post('/domains', { domainName, appPort, description });
      return data.data.domain;
    },
    [],
  );

  return useApi<Domain>(registerFn);
}

export function useVerifyDns() {
  const verifyFn = useCallback(async (domainId: string): Promise<DnsVerifyResult> => {
    const { data } = await api.post(`/domains/${domainId}/verify-dns`);
    return data.data;
  }, []);

  return useApi<DnsVerifyResult>(verifyFn);
}

export function useProvisionSsl() {
  const provisionFn = useCallback(async (domainId: string): Promise<Domain> => {
    const { data } = await api.post(`/domains/${domainId}/provision-ssl`);
    return data.data.domain;
  }, []);

  return useApi<Domain>(provisionFn);
}

export function useCheckSslStatus() {
  const checkFn = useCallback(async (domainId: string): Promise<SslStatusResult> => {
    const { data } = await api.get(`/domains/${domainId}/ssl-status`);
    return data.data;
  }, []);

  return useApi<SslStatusResult>(checkFn);
}

export function useProvisioningLog() {
  const fetchFn = useCallback(async (domainId: string) => {
    const { data } = await api.get(`/domains/${domainId}/provisioning-log`);
    return data.data;
  }, []);

  return useApi(fetchFn);
}

export function useUpdateDomain() {
  const updateFn = useCallback(async (domainId: string, description: string): Promise<Domain> => {
    const { data } = await api.patch(`/domains/${domainId}`, { description });
    return data.data.domain;
  }, []);

  return useApi<Domain>(updateFn);
}

export function useDeleteDomain() {
  const deleteFn = useCallback(async (domainId: string) => {
    const { data } = await api.delete(`/domains/${domainId}`);
    return data.data;
  }, []);

  return useApi(deleteFn);
}